<template>
  <main v-if="page" :class="page.padding && [config.padding[page.padding]]" role="main">
    <div class="mr-2">
      <slot name="language-switcher" />
    </div>
    <cms-layout-article v-if="page.templateName === 'article'" :page>
      <cms-section
        v-for="(section, key, i) in page?.sections"
        :key="section.name"
        :first-section="i === 0"
        page-context
        :section
      />
    </cms-layout-article>

    <cms-layout-article-v2 v-else-if="page.templateName === 'article-v2'" :page>
      <cms-section
        v-for="(section, key, i) in page?.sections"
        :key="section.name"
        :first-section="i === 0"
        page-context
        :section
      />
    </cms-layout-article-v2>

    <cms-section
      v-else
      v-for="(section, key, i) in page?.sections"
      :key="section.name"
      :first-section="i === 0"
      page-context
      :section
    />
  </main>
</template>

<script lang="ts" setup>
import type { PageContent } from '#types/page'

definePageMeta({
  validate({ path }) {
    return !/\.\w{2,5}$/.test(path)
  },
  async middleware(to) {
    const { getPage, getSeoMetadata } = useCms(to)
    const cmsPageState = useState<PageContent>('cmsPage')
    const pageError = useState<any>('cmsPageError')
    try {
      pageError.value = null
      const page = await getPage(to.params.slug)

      if (page) {
        const { metadata, link } = getSeoMetadata(page)
        useSeoMeta(metadata)
        useHead(() => ({ link }))
        cmsPageState.value = page
        to.meta.header = {
          transparent: page.overlayHeader,
          mode: page.headerStyle || 'light'
        }
      }
    }
    catch (error) {
      pageError.value = error
    }
  }
})

const page = useState<PageContent>('cmsPage').value
const pageError = useState<PageContent>('cmsPageError')

const route = useRoute()
const config = useAppConfig().pages.cms
const slug = useRouteParams<string>('slug')
const { $gtm, $sendExtraMonetateEvents, $optimizely, $feature } = useNuxtApp()
const cart = useCartStore()
const { trackEvent } = useOptimizely()
const { getUserEventData } = useAnalytics()

// defines markup via unhead-schema for search functionality on homepage
// https://unhead.unjs.io/schema-org/getting-started/how-it-works
useSchemaOrg([
  defineWebSite({
    potentialAction: [
      defineSearchAction({
        target: '/search?q={search_term_string}'
      })
    ]
  })
])

watch(pageError, () => {
  if (pageError.value)
    throw createError({ ...pageError.value, fatal: true })
}, { immediate: true })

onMounted(async () => {
  $gtm.push('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    pageCategory: slug.value ? 'Content' : 'Home',
    pageName: slug.value ? page?.name : 'Home'
  })

  const mainSlug = slug.value?.[0]

  if ($feature.monetateConfig?.extraMonetateEventsPages?.includes(mainSlug) || !mainSlug)
    $sendExtraMonetateEvents()

  const userEventData = await getUserEventData()
  $gtm.push('user.onLoadUserData', userEventData)
  $gtm.push('page.onRouterChange', route)
  $gtm.push('cart.onCartLoad', cart)
  $optimizely.dataReady(userEventData)
  trackEvent('page_view', { URL: mainSlug || route.fullPath })
})
</script>
